import React, { useEffect, useState } from 'react';
import { loadable } from '@shared/core/loadableFromNamedExport';
import type { BurgerMenuDrawerProps } from './UniversalBurgerMenu.types';

const LazyBurgerMenuDrawerInner = loadable(() => import(/* webpackChunkName: "admin/UniversalBurgerMenuDrawer" */ './BurgerMenuDrawer'), 'BurgerMenuDrawer');

/**
 * Defer the drawer — the chunk *and* the queries inside it — until the menu is first opened.
 *
 * The burger trigger sits on every admin page, but nothing it renders comes from the drawer's data:
 * `BurgerMenuDrawer` is where `useUniversalBurgerMenuController()` lives, and with it the event
 * query plus the whole product-row cluster (messaging tier, stationery counts and drafts, print
 * orders, cash funds, seating entitlement). Mounted eagerly, that all fires on page load behind a
 * closed drawer.
 *
 * `isInitialized` is sticky on purpose, and the gate is here rather than around `BurgerMenu`:
 * - Sticky, because re-skipping on close would throw the data away and make every re-open slow.
 *   Apollo's cache then serves the second open with no new request.
 * - Here, because `BurgerMenu` owns `isOpen` via `useDisclosure`. Unmounting it to flip this gate
 *   would reset that state and close the drawer on the very click that opened it.
 *
 * The `isInitialized` state is what defers the chunk: `loadable()` starts its `import()` as soon as
 * it renders, `isOpen` or not, so rendering `LazyBurgerMenuDrawerInner` with `isOpen={false}` would
 * still download it on every admin page.
 *
 * The guard reads `props.isOpen` alongside the state so the render that first sees the menu open
 * mounts the loadable. Gating on `isInitialized` alone would return null for that render and only
 * begin the download after the effect ran and a frame was painted.
 */
export const LazyBurgerMenuDrawer = (props: BurgerMenuDrawerProps) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (props.isOpen) setIsInitialized(true);
  }, [props.isOpen]);

  if (!isInitialized && !props.isOpen) return null;
  return <LazyBurgerMenuDrawerInner {...props} />;
};
